import React, { memo, useContext, useEffect, useRef } from 'react'

import { Button, Icon, Account, Close, classnames } from '@cgtrader/triangle-ui/dist/src/ssr-index'

import { iconSizeMedium } from 'Pods/ThreeDStudio/constants'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'
import { closeImageAssistant } from 'Pods/ThreeDStudio/Store/actions/ui'
import { IMAGE_ASSISTANT_TAB } from 'Pods/ThreeDStudio/Store/constants'
import ThreeDStudioPageContext from 'Pods/ThreeDStudio/ThreeDStudioPageContext'

import CreateTab from './Components/CreateTab/CreateTab'
import EditTab from './Components/EditTab/EditTab'
import SearchTab from './Components/SearchTab/SearchTab'
import Tabs from './Components/Tabs/Tabs'

const closeIcon = <Icon svgComponent={Close} size={iconSizeMedium} notSpaced />

function ImageAssistantPanel() {
  const { ideationFlowEnabled } = useContext(ThreeDStudioPageContext)
  const isOpen = useThreeDStudioStore(state => state.ui.isImageAssistantOpen)
  const activeTab = useThreeDStudioStore(state => state.ui.imageAssistantTab)

  const panelRef = useRef(null)
  const triggerRef = useRef(null)

  // Non-modal side panel: it intentionally leaves the Generate form usable, so
  // it has no focus trap. It still closes on Escape, moves focus into itself on
  // open, and restores focus to the trigger on close.
  useEffect(() => {
    if (!isOpen) {
      return undefined
    }

    triggerRef.current = document.activeElement
    panelRef.current?.focus()

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeImageAssistant()
      }
    }
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      // Only pull focus back to the trigger if it is still inside the panel that
      // is closing, so we never yank focus away from elsewhere on the page.
      const panel = panelRef.current
      if (panel?.contains(document.activeElement)) {
        triggerRef.current?.focus?.()
      }
    }
  }, [isOpen])

  if (!ideationFlowEnabled) {
    return null
  }

  const isCreate = activeTab === IMAGE_ASSISTANT_TAB.CREATE
  const isEdit = activeTab === IMAGE_ASSISTANT_TAB.EDIT
  const isSearch = !isCreate && !isEdit

  return (
    <div
      ref={panelRef}
      className={classnames('image-assistant-panel', isOpen && 'image-assistant-panel--opened')}
      role="dialog"
      aria-label="AI Assistant"
      aria-hidden={!isOpen}
      tabIndex={-1}
    >
      <div className="image-assistant-panel__header">
        <span className="image-assistant-panel__title cgt-heading-50">
          <Icon svgComponent={Account} size={iconSizeMedium} notSpaced />
          AI Assistant
        </span>
        <Button
          type="ghost"
          variant="only-icon"
          size="small"
          icon={closeIcon}
          aria-label="Close"
          onClick={closeImageAssistant}
        />
      </div>
      {/* Keying on isOpen remounts the tab content on close, resetting the
          SearchTab's internal navigation (query, selected category/image). */}
      <div className="image-assistant-panel__content" key={isOpen ? 'open' : 'closed'}>
        <div className="image-assistant-panel__intro">
          <p className="image-assistant-panel__subtitle cgt-body-base-semibold">
            Create or select a reference image to improve 3D results
          </p>
          <Tabs activeTab={activeTab} />
        </div>
        {isSearch && <SearchTab />}
        {isEdit && <EditTab />}
        {/* CreateTab stays mounted while the panel is open so its conversation
            survives tab switches; hidden (not unmounted) when another tab is
            active. It still resets on close via the __content key above. */}
        <div
          className={classnames(
            'image-assistant-panel__create-host',
            !isCreate && 'image-assistant-panel__create-host--hidden',
          )}
        >
          <CreateTab />
        </div>
      </div>
    </div>
  )
}

export default memo(ImageAssistantPanel)
